// Brand key persistence: `cr_brand` cookie on the root domain, shared by every app.
// Only the key is stored — phone/logo are resolved by key via the b2b brand API,
// so a tampered cookie or link can't inject arbitrary values.

export const BRAND_COOKIE = 'cr_brand';
export const BRAND_PARAM = 'brand';

const MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
const KEY_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;

interface StoredBrand {
  // Brand key, e.g. a b2b partner slug
  k: string;
  // utm_source the brand arrived with; a visit with a different one resets the brand
  s: string;
}

export function isValidBrandKey(key: string | null | undefined): key is string {
  return !!key && KEY_PATTERN.test(key);
}

export function parseBrandCookie(value: string | undefined | null): StoredBrand | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(value));
    return isValidBrandKey(parsed?.k) ? { k: parsed.k, s: typeof parsed.s === 'string' ? parsed.s : '' } : null;
  } catch {
    return null;
  }
}

function readStoredBrand(cookieString: string): StoredBrand | null {
  const match = cookieString.match(new RegExp(`(?:^|;\\s*)${BRAND_COOKIE}=([^;]*)`));
  return parseBrandCookie(match?.[1]);
}

// Reads the brand key from a raw Cookie header (SSR) or document.cookie
export function getBrandKeyFromCookieString(cookieString: string): string | null {
  return readStoredBrand(cookieString)?.k ?? null;
}

export function getBrandKey(): string | null {
  if (typeof document === 'undefined') return null;
  return getBrandKeyFromCookieString(document.cookie);
}

export function setBrandKey(key: string, utmSource = ''): void {
  if (!isValidBrandKey(key)) return;
  writeCookie(encodeURIComponent(JSON.stringify({ k: key, s: utmSource })), MAX_AGE_SECONDS);
}

export function clearBrandKey(): void {
  writeCookie('', 0);
}

// Call once on app start. Rules:
// 1. ?brand=<key> sets the brand (remembering the visit's utm_source)
// 2. a utm_source different from the one the brand arrived with resets it
// 3. otherwise (internal navigation, same utm_source) the brand is kept
export function captureBrand(search = typeof window !== 'undefined' ? window.location.search : ''): string | null {
  if (typeof document === 'undefined') return null;
  const params = new URLSearchParams(search);
  const utmSource = params.get('utm_source') || '';
  const key = params.get(BRAND_PARAM)?.toLowerCase();

  if (isValidBrandKey(key)) {
    setBrandKey(key, utmSource);
    return key;
  }

  const stored = readStoredBrand(document.cookie);
  if (stored && utmSource && utmSource !== stored.s) {
    clearBrandKey();
    return null;
  }
  return stored?.k ?? null;
}

function writeCookie(value: string, maxAge: number) {
  const domain = getRootDomain();
  document.cookie =
    `${BRAND_COOKIE}=${value}; path=/; max-age=${maxAge}; SameSite=Lax` +
    (domain ? `; domain=${domain}` : '') +
    (location.protocol === 'https:' ? '; Secure' : '');
}

// "app.coverright.com" → ".coverright.com"; host-only on localhost/IPs
function getRootDomain(): string | undefined {
  const host = location.hostname;
  if (host === 'localhost' || /^[\d.]+$/.test(host) || host.startsWith('[')) return undefined;
  const parts = host.split('.');
  return parts.length >= 2 ? '.' + parts.slice(-2).join('.') : undefined;
}
